import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, FileText, Network, ScanLine, Search, User } from '@/components/icons';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageHeader from '@/components/haccp/PageHeader';
import EmptyState from '@/components/haccp/EmptyState';
import EntityBadge from '@/components/haccp/EntityBadge';
import TraceabilityPanel from '@/components/haccp/TraceabilityPanel';
import HaccpReportsPanel from '@/components/haccp/HaccpReportsPanel';
import { ErrorState, LoadingState } from '@/components/haccp/AsyncState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const labels = {
  lot_created: 'Lotto creato',
  lot_use: 'Utilizzo lotto',
  lot_load: 'Carico lotto',
  lot_adjustment: 'Rettifica lotto',
  lot_created_from_ocr: 'Lotto da OCR',
  batch_created: 'Produzione registrata',
  ocr_upload: 'Scansione OCR'
};
const VALID_TABS = new Set(['activity', 'traceability', 'reports']);

export default function RegistroPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'activity';
  const activeTab = VALID_TABS.has(requestedTab) ? requestedTab : 'activity';
  const initialLotId = searchParams.get('lot_id') || '';
  const initialQuery = searchParams.get('q') || '';

  const changeTab = value => {
    const next = new URLSearchParams(searchParams);
    if (value === 'activity') next.delete('tab'); else next.set('tab', value);
    if (value !== 'traceability' && value !== 'reports') next.delete('lot_id');
    setSearchParams(next, { replace: true });
  };
  const changeLot = lotId => {
    const next = new URLSearchParams(searchParams);
    if (lotId) next.set('lot_id', lotId); else next.delete('lot_id');
    setSearchParams(next, { replace: true });
  };

  return <div data-testid="registro-page" className="app-page">
    <div className="app-page-inner">
      <PageHeader title="Registro HACCP" description="Attività, tracciabilità e report HACCP in un unico punto." icon={ClipboardList}/>
      <Tabs value={activeTab} onValueChange={changeTab} className="space-y-4">
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex h-auto">
          <TabsTrigger value="activity" className="gap-2"><ClipboardList className="h-4 w-4"/>Attività</TabsTrigger>
          <TabsTrigger value="traceability" className="gap-2"><Network className="h-4 w-4"/>Tracciabilità</TabsTrigger>
          <TabsTrigger value="reports" className="gap-2"><FileText className="h-4 w-4"/>Report</TabsTrigger>
        </TabsList>
        <TabsContent value="activity" className="mt-0"><ActivityLog/></TabsContent>
        <TabsContent value="traceability" className="mt-0"><TraceabilityPanel initialLotId={initialLotId} initialQuery={initialQuery} onLotChange={changeLot}/></TabsContent>
        <TabsContent value="reports" className="mt-0"><HaccpReportsPanel initialLotId={initialLotId} onLotChange={changeLot}/></TabsContent>
      </Tabs>
    </div>
  </div>;
}

function ActivityLog() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = filter !== 'all' ? `?entity=${filter}` : '';
      const r = await fetch(`${BACKEND_URL}/api/movements/audit-log${q}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Impossibile caricare il registro');
      setEntries(await r.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => entries.filter(e => !search || JSON.stringify(e).toLowerCase().includes(search.toLowerCase())), [entries, search]);
  const entity = e => ['lot', 'recipe', 'batch'].includes(e) ? e : null;

  return <div className="space-y-4">
    <div className="toolbar">
      <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/><Input className="pl-9" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca nel registro" aria-label="Cerca nel registro"/></div>
      <Select value={filter} onValueChange={setFilter}><SelectTrigger className="sm:w-52"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="all">Tutte le entità</SelectItem><SelectItem value="lot">Lotti</SelectItem><SelectItem value="batch">Produzioni</SelectItem><SelectItem value="ocr_document">OCR</SelectItem><SelectItem value="recipe">Ricette</SelectItem></SelectContent></Select>
    </div>
    {loading ? <LoadingState/> : error ? <ErrorState description={error} onRetry={load}/> : rows.length === 0 ? <EmptyState icon={ClipboardList} title="Nessuna attività trovata" description="Il registro si popolerà con le operazioni di tracciabilità."/> : <div className="surface divide-y">{rows.map(e => <div key={e.audit_id} className="p-3 sm:p-4 flex items-start gap-3">{entity(e.entity) ? <EntityBadge entity={entity(e.entity)} compact/> : <div className="h-9 w-9 rounded-lg border bg-slate-50 flex items-center justify-center">{e.entity === 'ocr_document' ? <ScanLine className="h-4 w-4 text-primary"/> : e.entity === 'user' ? <User className="h-4 w-4 text-primary"/> : <ClipboardList className="h-4 w-4 text-primary"/>}</div>}<div className="flex-1 min-w-0"><div className="flex flex-wrap justify-between gap-2"><p className="font-medium">{labels[e.action] || e.action}</p><time className="text-xs text-muted-foreground">{new Date(e.timestamp).toLocaleString('it-IT')}</time></div><p className="mono-id mt-1">{e.entity}/{e.entity_id}</p>{e.details && Object.keys(e.details).length > 0 && <p className="text-xs text-muted-foreground mt-2 break-words">{Object.entries(e.details).slice(0, 4).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}</p>}</div></div>)}</div>}
  </div>;
}
